import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Modal, Form, Button } from 'react-bootstrap';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import translations from './translations';
import { useAlertContext } from '../../contexts/AlertContext';

const resetText = {
  pt: {
    forgot: 'Esqueceu a senha?',
    sendReset: 'Enviar e-mail de redefinição',
    email: 'E-mail',
    enterEmail: 'Digite seu e-mail',
    resetSuccess: 'E-mail de redefinição enviado com sucesso.',
    notFound: 'E-mail não encontrado.',
    resetErrorGeneric: 'Erro ao enviar o e-mail. Tente novamente mais tarde.',
    resetEmailRequired: 'Informe um e-mail válido.',
    back: 'Voltar ao login',
    noAccount: 'Não tem conta?',
    register: 'Registre-se',
    appName: 'Bioinsumos na Cafeicultura',
    hint: 'Entre com suas credenciais para continuar.',
    hintForgot: 'Enviaremos um link de redefinição para o seu e-mail.',
  },
  en: {
    forgot: 'Forgot password?',
    sendReset: 'Send reset email',
    email: 'Email',
    enterEmail: 'Enter your email',
    resetSuccess: 'Reset email sent successfully.',
    notFound: 'Email not found.',
    resetErrorGeneric: 'Error sending the email. Please try again later.',
    resetEmailRequired: 'Enter a valid email.',
    back: 'Back to login',
    noAccount: "Don't have an account?",
    register: 'Sign up',
    appName: 'Bioinputs in Coffee Growing',
    hint: 'Sign in with your credentials to continue.',
    hintForgot: 'We will send a reset link to your email.',
  },
};

export default function LoginModal() {
  const navigate = useNavigate();
  const {
    loginModalOpen, setLoginModalOpen,
    setIsLoggedIn, setUsername: setContextUsername,
    setRole: setContextRole, setName: setContextName,
  } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;
  const r = resetText[language] || resetText.pt;

  const [view, setView] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoOk, setLogoOk] = useState(true);

  const [email, setEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetOk, setResetOk] = useState(false);

  const close = () => {
    setLoginModalOpen(false);
    setView('login');
    setUsername('');
    setPassword('');
    setEmail('');
    setResetMsg('');
    setResetOk(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(API_ENDPOINTS.AUTH_LOGIN, { username, password });
      // O campo aceita e-mail ou username. Guardamos o username canônico devolvido pelo
      // backend — é ele que alimenta GET /api/users/username/{username} (perfil, capítulos).
      // Guardar o texto digitado quebraria essas telas em quem entrou pelo e-mail.
      const canonicalUsername = response.data.username || username;
      const auth = btoa(`${username}:${password}`);
      localStorage.setItem('auth', auth);
      localStorage.setItem('name', response.data.name);
      localStorage.setItem('role', response.data.role);
      localStorage.setItem('username', canonicalUsername);
      setIsLoggedIn(true);
      setContextUsername(canonicalUsername);
      setContextRole(response.data.role);
      setContextName(response.data.name);
      showSuccess(t.welcome + response.data.name + '!');
      close();
      navigate(`/${response.data.role.toLowerCase()}`);
    } catch (error) {
      if (error.toString().includes('401')) {
        showError(t.errorInvalid);
      } else {
        showError(t.errorServer);
      }
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+/.test(String(v).toLowerCase());

  const handleReset = async () => {
    setResetMsg('');
    if (!email || !validateEmail(email)) {
      setResetMsg(r.resetEmailRequired);
      return;
    }
    const baseUrl = window.location.href.split('?')[0]
      .replace('https://', '').replace('http://', '').replace('/login', '');
    try {
      const response = await axios.post(
        API_ENDPOINTS.RESET_PASSWORD(baseUrl),
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.status === 200) {
        setResetOk(true);
        setResetMsg(r.resetSuccess);
        showSuccess(r.resetSuccess);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setResetMsg(r.notFound);
      } else {
        setResetMsg(r.resetErrorGeneric);
      }
    }
  };

  return (
    <Modal show={!!loginModalOpen} onHide={close} centered>
      <Modal.Header closeButton className="border-0 pb-0" />
      <Modal.Body className="px-4 pb-4 pt-2">
        <div className="auth-brand">
          {logoOk && (
            <img
              src={process.env.PUBLIC_URL + '/biotrop-icon.png'}
              alt="Biotrop"
              className="brand-logo-lg mb-2"
              onError={() => setLogoOk(false)}
            />
          )}
          <span className="auth-brand-name">BIOTROP</span>
          <span className="auth-brand-tagline">Soluções em Tecnologia Biológica</span>
        </div>

        <div className="text-center mb-4">
          <div className="auth-app-name">{r.appName}</div>
          <div className="auth-app-hint">{view === 'login' ? r.hint : r.hintForgot}</div>
        </div>

        {view === 'login' ? (
          <form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <Form.Label>{t.name}</Form.Label>
              <Form.Control
                type="text"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t.namePlaceholder || t.name}
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>{t.password}</Form.Label>
              <Form.Control
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.passwordPlaceholder || t.password}
              />
            </Form.Group>
            <div className="d-grid">
              <Button type="submit" size="lg" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {t.button}
                  </>
                ) : t.button}
              </Button>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <Button variant="link" size="sm" className="p-0" onClick={() => setView('forgot')}>
                {r.forgot}
              </Button>
              <small className="text-muted">
                {r.noAccount}{' '}
                <Link to="/register" onClick={close}>{r.register}</Link>
              </small>
            </div>
          </form>
        ) : (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>{r.email}</Form.Label>
              <Form.Control
                type="email"
                placeholder={r.enterEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={resetOk}
                autoFocus
              />
            </Form.Group>
            {resetMsg && (
              <div className={`alert ${resetOk ? 'alert-success' : 'alert-danger'} py-2`} role="alert">
                {resetMsg}
              </div>
            )}
            {!resetOk && (
              <div className="d-grid mb-3">
                <Button size="lg" onClick={handleReset}>{r.sendReset}</Button>
              </div>
            )}
            <Button variant="link" size="sm" className="p-0" onClick={() => setView('login')}>
              ← {r.back}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
